import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { colors, gradients, shadows } from '../theme/designSystem';

interface KPICardProps {
  title: string;
  value: number | string;
  unit?: string;
  subtitle?: string;
  gradient?: string;
  trend?: number;
  icon?: React.ReactNode;
}

const KPICard: React.FC<KPICardProps> = ({ 
  title, 
  value, 
  unit, 
  subtitle, 
  gradient = gradients.primary,
  trend,
  icon 
}) => {
  return (
    <Card 
      sx={{ 
        height: '100%',
        borderRadius: '12px',
        border: `1px solid ${colors.neutral[200]}`,
        boxShadow: shadows.card,
        transition: 'box-shadow 0.3s ease',
        '&:hover': {
          boxShadow: shadows.cardHover,
        },
      }}
    >
      <CardContent sx={{ p: '20px' }}>
        {/* Icon Container */}
        {icon && (
          <Box
            sx={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              mb: 2,
              '& svg': {
                fontSize: '24px',
              },
            }}
          >
            {icon}
          </Box>
        )}

        {/* Label */}
        <Typography 
          sx={{ 
            fontSize: '0.875rem',
            color: colors.neutral[500],
            fontWeight: 500,
            mb: 1,
          }}
        >
          {title}
        </Typography>

        {/* Value */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
          <Typography 
            sx={{ 
              fontSize: '2.5rem',
              fontWeight: 700,
              color: colors.neutral[900],
              lineHeight: 1,
            }}
          >
            {typeof value === 'number' ? value.toFixed(2) : value}
          </Typography>
          {unit && (
            <Typography 
              sx={{ 
                fontSize: '1rem',
                color: colors.neutral[500],
                fontWeight: 500,
              }}
            >
              {unit}
            </Typography>
          )}
        </Box>

        {/* Subtitle or Trend */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {trend !== undefined && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: trend >= 0 ? colors.status.success : colors.status.error,
              }}
            >
              {trend >= 0 ? (
                <TrendingUpIcon sx={{ fontSize: '12px' }} />
              ) : (
                <TrendingDownIcon sx={{ fontSize: '12px' }} />
              )}
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                {Math.abs(trend).toFixed(1)}%
              </Typography>
            </Box>
          )}
          {subtitle && (
            <Typography 
              sx={{ 
                fontSize: '0.75rem',
                color: colors.neutral[500],
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default KPICard;
